'use client';

import * as React from 'react';
import Image from 'next/image';
import AutoScroll from 'embla-carousel-auto-scroll';
import gsap from 'gsap';
import type { LucideIcon } from 'lucide-react';
import { Globe } from 'lucide-react';
import { stripHtml, getImageUrl } from '@/lib/homepage-mappers';
import type {
  SustainabilityColorfulCardWrapper,
  SustainabilityColorfulCard,
} from '@/types/sustainability';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { cn } from '@/components/ui/utils';

export type SDGSlide = {
  id: string;
  number: number;
  titleLine: string;
  imageSrc: string;
  Icon: LucideIcon;
  backTitle: string;
  cardColor?: string;
  backDescription: string;
};

const DEFAULT_ICON: LucideIcon = Globe;

export type SDGDetailSection = {
  label: string;
  body: string;
};

const SDG_SECTION_PATTERN = /\b(Environment|Social|Governance)\s*:/gi;

export function parseSdgDescription(description: string): SDGDetailSection[] {
  const text = description.replace(/\s+/g, ' ').trim();
  if (!text) return [];

  const matches = Array.from(text.matchAll(SDG_SECTION_PATTERN));
  if (matches.length === 0) {
    return [{ label: '', body: text }];
  }

  return matches
    .map((match, index) => {
      const label = match[1];
      const start = (match.index ?? 0) + match[0].length;
      const end =
        index + 1 < matches.length ? (matches[index + 1].index ?? text.length) : text.length;
      const body = text.slice(start, end).trim();

      if (!body) return null;

      return {
        label: `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}`,
        body,
      };
    })
    .filter((section): section is SDGDetailSection => section !== null);
}

function SDGBackSections({ description }: { description: string }) {
  const sections = React.useMemo(() => parseSdgDescription(description), [description]);

  if (sections.length === 0) return null;

  if (sections.length === 1 && !sections[0].label) {
    return (
      <div className="mt-4 text-[11px] leading-relaxed text-white/92 sm:mt-6 sm:text-[12px]">
        <p>{sections[0].body}</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3 text-[11px] leading-relaxed text-white/92 sm:mt-6 sm:space-y-4 sm:text-[12px]">
      {sections.map((section) => (
        <p key={section.label}>
          <span className="font-bold text-white">{section.label}:</span> {section.body}
        </p>
      ))}
    </div>
  );
}

export function SDGFlipCard({
  slide,
  onHoverDelta,
  flipInnerHeightClassName,
}: {
  slide: SDGSlide;
  onHoverDelta: (delta: number) => void;
  /** Override flip panel height (default matches main sustainability SDG grid). */
  flipInnerHeightClassName?: string;
}) {
  const flipInnerRef = React.useRef<HTMLDivElement>(null);
  const tweenRef = React.useRef<gsap.core.Tween | null>(null);
  const [isFlipped, setIsFlipped] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = flipInnerRef.current;
    if (!el) return;
    gsap.set(el, {
      rotationY: 0,
      transformOrigin: '50% 50%',
      transformPerspective: 1400,
      force3D: true,
    });
  }, []);

  const setFlipped = React.useCallback((open: boolean) => {
    const el = flipInnerRef.current;
    if (!el) return;
    tweenRef.current?.kill();
    tweenRef.current = gsap.to(el, {
      rotationY: open ? 180 : 0,
      duration: 0.6,
      ease: 'power2.inOut',
      transformPerspective: 1400,
      force3D: true,
    });
  }, []);

  React.useEffect(() => {
    setFlipped(isFlipped);
  }, [isFlipped, setFlipped]);

  React.useEffect(() => {
    return () => {
      tweenRef.current?.kill();
    };
  }, []);

  return (
    <div
      className="h-full cursor-pointer [perspective:1400px]"
      role="button"
      tabIndex={0}
      aria-label={`Read SDG ${slide.number}: ${slide.titleLine}`}
      aria-pressed={isFlipped}
      onPointerEnter={(event) => {
        if (event.pointerType === 'touch') return;
        onHoverDelta(1);
        setIsFlipped(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        onHoverDelta(-1);
        setIsFlipped(false);
      }}
      onClick={() => {
        setIsFlipped((current) => !current);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsFlipped((current) => !current);
        }
      }}
    >
      <div
        ref={flipInnerRef}
        className={cn(
          'relative w-full transform-gpu will-change-transform',
          flipInnerHeightClassName ?? 'h-[360px] max-sm:h-[330px]',
        )}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div
          className={cn(
            'absolute inset-0 overflow-hidden rounded-xl bg-black shadow-lg',
            '[backface-visibility:hidden]',
          )}
          style={slide.cardColor ? { backgroundColor: slide.cardColor } : undefined}
        >
          {slide.imageSrc ? (
            <Image
              src={slide.imageSrc}
              alt={slide.titleLine}
              fill
              className="z-0 origin-center scale-[1] object-cover object-center sm:scale-[1]"
              sizes="(min-width: 1024px) 28vw, 85vw"
            />
          ) : null}
        </div>

        <div
          className={cn(
            'absolute inset-0 overflow-hidden rounded-xl p-4 text-white shadow-lg sm:p-5',
            '[transform:rotateY(180deg)] [backface-visibility:hidden]',
          )}
          style={{ backgroundColor: slide.cardColor || '#F39A2D' }}
        >
          <h3 className="text-[1.45rem] font-bold leading-[1.08] sm:text-[1.95rem]">
            {slide.backTitle}
          </h3>
          {slide.backDescription ? <SDGBackSections description={slide.backDescription} /> : null}
        </div>
      </div>
    </div>
  );
}

export function SDGMarqueeRow({
  slides,
  direction,
  className,
}: {
  slides: SDGSlide[];
  direction: 'forward' | 'backward';
  className?: string;
}) {
  const [api, setApi] = React.useState<CarouselApi>();
  const hoverDepth = React.useRef(0);
  const carouselHover = React.useRef(false);
  const resumeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const canAutoScroll = slides.length >= 3;

  const autoplay = React.useRef(
    AutoScroll({
      speed: 1.15,
      direction,
      startDelay: 0,
      stopOnMouseEnter: false,
      stopOnInteraction: false,
      playOnInit: true,
      stopOnFocusIn: false,
    }),
  );

  const getAutoScrollPlugin = React.useCallback(() => {
    return api?.plugins()?.autoScroll as { stop: () => void; play: () => void } | undefined;
  }, [api]);

  const syncAutoScroll = React.useCallback(() => {
    const plugin = getAutoScrollPlugin();
    if (!plugin) return;
    if (hoverDepth.current > 0 || carouselHover.current) plugin.stop();
    else plugin.play();
  }, [getAutoScrollPlugin]);

  const pauseAutoScrollTemporarily = React.useCallback(() => {
    const plugin = getAutoScrollPlugin();
    if (!plugin) return;
    plugin.stop();
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      resumeTimeoutRef.current = null;
      syncAutoScroll();
    }, 2200);
  }, [getAutoScrollPlugin, syncAutoScroll]);

  React.useEffect(() => {
    syncAutoScroll();
  }, [api, syncAutoScroll]);

  React.useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const onHoverDelta = React.useCallback(
    (delta: number) => {
      hoverDepth.current = Math.max(0, hoverDepth.current + delta);
      syncAutoScroll();
    },
    [syncAutoScroll],
  );

  const loopSlides = React.useMemo(
    () => (canAutoScroll ? [...slides, ...slides] : slides),
    [canAutoScroll, slides],
  );
  const needsNavigation = slides.length > 1;
  const loopEnabled = slides.length > 1;

  const navButtonClassName = cn(
    'top-1/2 z-20 size-10 -translate-y-1/2 touch-manipulation border-white/20 bg-black/80 text-white shadow-md backdrop-blur-[2px]',
    'pointer-events-auto opacity-100 transition-opacity duration-200',
    'hover:bg-black/90 hover:text-white active:scale-95',
    'sm:pointer-events-none sm:opacity-0 sm:size-9',
    'sm:group-hover/carousel:pointer-events-auto sm:group-hover/carousel:opacity-100',
    'sm:group-focus-within/carousel:pointer-events-auto sm:group-focus-within/carousel:opacity-100',
    'focus-visible:pointer-events-auto focus-visible:opacity-100',
    'disabled:pointer-events-none disabled:opacity-25',
  );

  if (slides.length === 0) return null;

  return (
    <div
      className={cn('group/carousel relative overflow-hidden', className)}
      onPointerEnter={(event) => {
        // Touch can fire enter without a reliable leave and would leave the marquee stuck.
        if (event.pointerType === 'touch') return;
        carouselHover.current = true;
        syncAutoScroll();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        carouselHover.current = false;
        syncAutoScroll();
      }}
    >
      <Carousel
        key={canAutoScroll ? 'sdg-auto' : 'sdg-manual'}
        setApi={setApi}
        opts={{
          loop: loopEnabled,
          align: 'start',
          containScroll: canAutoScroll ? false : 'trimSnaps',
          dragFree: canAutoScroll,
          skipSnaps: false,
        }}
        plugins={canAutoScroll ? [autoplay.current] : []}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-3">
          {loopSlides.map((slide, index) => (
            <CarouselItem
              key={index}
              className="basis-[72%] pl-2 sm:basis-[44%] md:basis-[31%] lg:basis-[24%] xl:basis-[20%] md:pl-3"
            >
              <SDGFlipCard slide={slide} onHoverDelta={onHoverDelta} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {needsNavigation ? (
          <>
            <CarouselPrevious
              type="button"
              variant="outline"
              disabled={false}
              className={cn(navButtonClassName, 'left-2 sm:left-4')}
              onPointerDown={(event) => {
                event.stopPropagation();
                pauseAutoScrollTemporarily();
              }}
            />
            <CarouselNext
              type="button"
              variant="outline"
              disabled={false}
              className={cn(navButtonClassName, 'right-2 sm:right-4')}
              onPointerDown={(event) => {
                event.stopPropagation();
                pauseAutoScrollTemporarily();
              }}
            />
          </>
        ) : null}
      </Carousel>
    </div>
  );
}

export interface SustainabilitySDGsSectionProps {
  heading?: string;
  intro?: string;
  showLogo?: boolean;
  cardWrapper?: SustainabilityColorfulCardWrapper;
}

export function cmsCardToSlide(card: SustainabilityColorfulCard): SDGSlide {
  const titleMatch = card.title?.match(/\d+/);
  const goalNumber = titleMatch ? parseInt(titleMatch[0], 10) : 0;
  const imageSrc =
    getImageUrl(card.numberIcon as never) || getImageUrl(card.bgImage as never) || '';

  return {
    id: String(card.id),
    number: goalNumber,
    titleLine: card.subHeading || card.title,
    imageSrc,
    Icon: DEFAULT_ICON,
    backTitle: card.title,
    cardColor: card.cardColor,
    backDescription: stripHtml(card.description),
  };
}

function SDGSectionHeader({
  resolvedHeading,
  resolvedIntro,
  showLogo,
  logoUrl,
}: {
  resolvedHeading: string;
  resolvedIntro: string;
  showLogo: boolean;
  logoUrl: string;
}) {
  if (!(resolvedHeading || resolvedIntro || (showLogo && logoUrl))) return null;
  return (
    <div className="container mx-auto flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-start lg:justify-between max-sm:px-4">
      {resolvedHeading || resolvedIntro ? (
        <div className="max-w-3xl">
          {resolvedHeading ? (
            <h2 className="text-[1.75rem] font-bold tracking-tight text-white sm:text-4xl md:text-[2.35rem]">
              {resolvedHeading}
            </h2>
          ) : null}
          {resolvedIntro ? (
            <p className="mt-3 text-sm leading-relaxed text-neutral-300 sm:mt-4 sm:text-base md:text-lg">
              {resolvedIntro}
            </p>
          ) : null}
        </div>
      ) : null}
      {showLogo && logoUrl ? (
        <div className="relative shrink-0 lg:mt-0 lg:self-start">
          <Image
            src={logoUrl}
            alt={resolvedHeading || 'SDG logo'}
            width={320}
            height={320}
            className="mx-auto h-auto w-[min(170px,52vw)] object-contain object-center sm:mx-0 sm:w-[min(220px,55vw)] sm:object-left lg:object-right"
            priority={false}
          />
        </div>
      ) : null}
    </div>
  );
}

export default function SustainabilitySDGsSection({
  heading,
  intro,
  showLogo = true,
  cardWrapper,
}: SustainabilitySDGsSectionProps) {
  const resolvedHeading = heading?.trim() || cardWrapper?.title?.trim() || '';
  const resolvedIntro = intro?.trim() || stripHtml(cardWrapper?.description) || '';
  const logoUrl = getImageUrl(cardWrapper?.headingIcon as never) || '';

  const topSlides = React.useMemo(
    () => (cardWrapper?.cardsRow1 ?? []).map(cmsCardToSlide),
    [cardWrapper?.cardsRow1],
  );
  const bottomSlides = React.useMemo(
    () => (cardWrapper?.cardRow2 ?? []).map(cmsCardToSlide),
    [cardWrapper?.cardRow2],
  );

  if (!resolvedHeading && !resolvedIntro && topSlides.length === 0 && bottomSlides.length === 0) {
    return null;
  }

  return (
    <section className="relative z-10 bg-black text-white">
      <div className="w-full">
        <SDGSectionHeader
          resolvedHeading={resolvedHeading}
          resolvedIntro={resolvedIntro}
          showLogo={showLogo}
          logoUrl={logoUrl}
        />

        {topSlides.length > 0 || bottomSlides.length > 0 ? (
          <div className="mt-8 space-y-5 sm:mt-10 sm:space-y-8 md:mt-14 md:space-y-10">
            {topSlides.length > 0 ? <SDGMarqueeRow slides={topSlides} direction="forward" /> : null}
            {bottomSlides.length > 0 ? (
              <SDGMarqueeRow slides={bottomSlides} direction="backward" />
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
